import React, { Fragment, useEffect, useState } from "react";
import { Col, Container, Row, Form, FormGroup, Input, Label, Button } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import AxiosInstance from "../../services/AxiosInstance";
import logoWhite from "../../assets/images/logo/logo.png";
import "react-toastify/dist/ReactToastify.css";

const styles = {
    section: {
        background: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    card: {
        background: '#fff',
        width: '450px',
        padding: '40px',
        borderRadius: '20px',
        boxShadow: '0 15px 35px rgba(0,0,0,0.5)',
    },
    title: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#2c3e50',
        marginBottom: '5px',
    },
    subtitle: {
        color: '#7f8c8d',
        marginBottom: '20px',
    },
    button: {
        height: '50px',
        fontSize: '16px',
        letterSpacing: '1px',
        borderRadius: '8px',
        backgroundColor: '#1a1a1a',
        border: 'none',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
        marginTop: '20px',
    },
    input: {
        borderRadius: '8px',
        padding: '12px',
    }
};

const AdminLoginPage = () => {
    const navigate = useNavigate();
    const [togglePassword, setTogglePassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: "",
        password: "",
    });

    useEffect(() => {
        try {
            const token = localStorage.getItem("_token");
            const rawUser = localStorage.getItem("user_data");
            if (!token || !rawUser) return;
            const user = JSON.parse(rawUser);
            const userType = String(user?.type || "").toUpperCase();
            if (userType.includes("ADMIN")) {
                navigate("/job/admin/dashboard", { replace: true });
            }
        } catch (e) {
            // Ignore malformed localStorage values and stay on login page.
        }
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setErrors((prev) => ({ ...prev, [name]: "" }));
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        const err = {};
        if (!formData.email) {
            err.email = "Email is required";
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,6}$/;
            if (!emailRegex.test(formData.email)) err.email = "Please enter a valid email";
        }
        if (!formData.password) err.password = "Password is required";
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        setErrors({});
        try {
            const response = await AxiosInstance().post("/auth/admin_login", {
                email: formData.email,
                password: formData.password,
            });
            const { status, message, data } = response.data;
            if (status && data) {
                localStorage.setItem("_token", data.token);
                localStorage.setItem("user_data", JSON.stringify({
                    name: data.name,
                    email: data.email,
                    type: data.type,
                    id: data.id,
                    user_role_id: data.user_role_id,
                    is_super: data.is_super,
                }));
                toast.success(message || "Logged in successfully.");
                navigate("/job/admin/dashboard", { replace: true });
            } else {
                toast.error(message || "Login failed.");
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message || "Login failed. Please try again.";
            toast.error(msg);
            if (error.response?.data?.data) setErrors(error.response.data.data);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Fragment>
            <section style={styles.section}>
                <Container fluid={true} className="p-0">
                    <Row className="m-0 justify-content-center">
                        <Col xl="12" className="p-0 d-flex justify-content-center">
                            <div style={styles.card}>
                                <div className="text-center mb-4">
                                    <Link className="logo" to={process.env.PUBLIC_URL}>
                                        <img
                                            className="img-fluid"
                                            src={logoWhite}
                                            alt="Admin Login"
                                            style={{ width: "180px" }}
                                        />
                                    </Link>
                                </div>
                                <Form className="theme-form login-form" onSubmit={handleSubmit}>
                                    <div className="text-center">
                                        <h4 style={styles.title}>Admin Control Panel</h4>
                                        <p style={styles.subtitle}>Secure access for Naukrify Administrators</p>
                                    </div>
                                    <hr style={{ margin: '20px 0', borderColor: '#eee' }} />
                                    <FormGroup>
                                        <Label className="col-form-label m-0">Admin Email</Label>
                                        <Input
                                            className="form-control"
                                            type="email"
                                            name="email"
                                            placeholder="admin@naukrify.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            style={styles.input}
                                        />
                                        {errors.email && (
                                            <small className="text-danger">{errors.email}</small>
                                        )}
                                    </FormGroup>
                                    <FormGroup className="position-relative">
                                        <Label className="col-form-label m-0">Password</Label>
                                        <div className="position-relative">
                                            <Input
                                                className="form-control"
                                                type={togglePassword ? "text" : "password"}
                                                name="password"
                                                placeholder="********"
                                                value={formData.password}
                                                onChange={handleChange}
                                                style={styles.input}
                                            />
                                            <div
                                                className="show-hide"
                                                style={{
                                                    position: 'absolute',
                                                    right: '15px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    cursor: 'pointer',
                                                    color: '#999'
                                                }}
                                                onClick={() => setTogglePassword(!togglePassword)}
                                            >
                                                <i className={togglePassword ? "fa fa-eye-slash" : "fa fa-eye"}></i>
                                            </div>
                                        </div>
                                        {errors.password && (
                                            <small className="text-danger">{errors.password}</small>
                                        )}
                                    </FormGroup>
                                    <FormGroup className="mb-0">
                                        <Button
                                            className="btn-block w-100"
                                            color="dark"
                                            type="submit"
                                            disabled={loading}
                                            style={styles.button}
                                        >
                                            {loading ? "AUTHENTICATING..." : "ADMIN LOGIN"}
                                        </Button>
                                    </FormGroup>
                                    <div className="text-center mt-4">
                                        <small className="text-muted">© {new Date().getFullYear()} Naukrify. All rights reserved.</small>
                                    </div>
                                </Form>
                            </div>
                        </Col>
                    </Row>
                </Container>
                <ToastContainer />
            </section>
        </Fragment>
    );
};


export default AdminLoginPage;
